import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  CircularProgress,
  LinearProgress,
  Chip,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { supabase } from '../supabaseClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

/**
 * Dialog for importing products from connected e-commerce stores.
 * 
 * This dialog:
 * 1. Lists only connected stores (with connection_status='connected')
 * 2. Allows user to select a store and trigger product import
 * 3. Shows import progress and status
 * 
 * Products are imported as markdown documents and fed through the existing
 * upload_service → chunk → embed pipeline.
 */
const PlatformImportDialog = ({ 
  open, 
  onClose, 
  onImportComplete,
  teamId = null  // Optional team scoping
}) => {
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState('');
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [importStatus, setImportStatus] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);

  // Get auth token
  const getAuthToken = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || '';
  }, []);

  // Load connected stores on open
  useEffect(() => {
    if (open) {
      loadConnectedStores();
      setError('');
      setSuccess('');
      setImportStatus(null);
      setSelectedStore('');
    } else {
      // Clear polling on close
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
    }
  }, [open]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const loadConnectedStores = async () => {
    setLoading(true);
    try {
      const token = await getAuthToken();
      const resp = await fetch(`${BACKEND_URL}/market-api/integrations/connected`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (resp.ok) {
        const data = await resp.json();
        setStores(data || []);
        
        // Auto-select if only one store
        if (data?.length === 1) {
          setSelectedStore(data[0].store_id);
        }
      } else {
        setError('Failed to load connected stores');
      }
    } catch (e) {
      console.error('Error loading stores:', e);
      setError('Failed to load stores');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!selectedStore) {
      setError('Please select a store');
      return;
    }

    setImporting(true);
    setError('');
    setSuccess('');
    setImportStatus({ status: 'starting', message: 'Starting import...' });

    try {
      const token = await getAuthToken();
      const resp = await fetch(`${BACKEND_URL}/market-api/integrations/${selectedStore}/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          team_id: teamId,
        })
      });

      const data = await resp.json();

      if (!resp.ok) {
        throw new Error(data.detail || data.error || 'Import failed');
      }

      setImportStatus({ status: 'running', message: 'Import in progress...' });
      
      // Start polling for status
      const interval = setInterval(() => pollImportStatus(selectedStore), 3000);
      setPollingInterval(interval);

    } catch (e) {
      setError(e.message || 'Failed to start import');
      setImporting(false);
      setImportStatus(null);
    }
  };

  const pollImportStatus = async (storeId) => {
    try {
      const token = await getAuthToken();
      const resp = await fetch(`${BACKEND_URL}/market-api/integrations/${storeId}/import-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (resp.ok) {
        const data = await resp.json();
        
        if (data.sync_status === 'completed') {
          setImportStatus({ 
            status: 'completed', 
            message: `Import completed! ${data.product_count} products imported.`,
            productCount: data.product_count
          });
          setSuccess(`Successfully imported ${data.product_count} products!`);
          setImporting(false);
          
          // Stop polling
          if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
          }
          
          // Notify parent
          if (onImportComplete) {
            onImportComplete(data);
          }
        } else if (data.sync_status === 'failed') {
          setImportStatus({ 
            status: 'failed', 
            message: data.error || 'Import failed'
          });
          setError(data.error || 'Import failed');
          setImporting(false);
          
          // Stop polling
          if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
          }
        } else {
          setImportStatus({ 
            status: 'running', 
            message: `Importing products... (${data.product_count || 0} so far)`,
            productCount: data.product_count
          });
        }
      }
    } catch (e) {
      console.error('Error polling status:', e);
    }
  };

  const handleClose = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    onClose();
  };

  const getSelectedStoreInfo = () => {
    return stores.find(s => s.store_id === selectedStore);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <StorefrontIcon color="primary" />
          Import Products from Store
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : stores.length === 0 ? (
          <Alert severity="info">
            No connected stores found. Please connect an e-commerce platform first in the Integrations settings.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select a connected store to import products. Products will be converted to documents 
              and added to your knowledge base for AI-powered answers.
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Store</InputLabel>
              <Select
                value={selectedStore}
                onChange={(e) => setSelectedStore(e.target.value)}
                label="Select Store"
                disabled={importing}
              >
                {stores.map((store) => (
                  <MenuItem key={store.store_id} value={store.store_id}>
                    <Box display="flex" alignItems="center" gap={1} width="100%">
                      <span>{store.store_name}</span>
                      <Chip 
                        label={store.platform_name} 
                        size="small" 
                        variant="outlined"
                        sx={{ ml: 'auto' }}
                      />
                      {store.product_count > 0 && (
                        <Chip 
                          label={`${store.product_count} products`} 
                          size="small" 
                          color="primary"
                        />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {selectedStore && getSelectedStoreInfo() && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2">
                  {getSelectedStoreInfo().store_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Platform: {getSelectedStoreInfo().platform_name}
                </Typography>
                {getSelectedStoreInfo().last_sync_at && (
                  <Typography variant="body2" color="text.secondary">
                    Last import: {new Date(getSelectedStoreInfo().last_sync_at).toLocaleString()}
                  </Typography>
                )}
                {getSelectedStoreInfo().product_count > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Previously imported: {getSelectedStoreInfo().product_count} products
                  </Typography>
                )}
              </Box>
            )}
            
            {/* Import Status */}
            {importStatus && (
              <Box sx={{ mt: 2 }}>
                {importStatus.status === 'running' && (
                  <>
                    <LinearProgress sx={{ mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      {importStatus.message}
                    </Typography>
                  </>
                )}
                {importStatus.status === 'completed' && (
                  <Alert 
                    severity="success" 
                    icon={<CheckCircleIcon />}
                  >
                    {importStatus.message}
                  </Alert>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={importing}>
          {success ? 'Close' : 'Cancel'}
        </Button>
        {stores.length > 0 && !success && (
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={!selectedStore || importing}
            startIcon={importing ? <CircularProgress size={16} /> : <SyncIcon />}
          >
            {importing ? 'Importing...' : 'Import Products'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PlatformImportDialog;
